/**
 * Thera Office Injector
 * Date: 11-14-2024
 * Author: Jake Michalski
 * Description: This file contains the functions to interact with the Thera Office EMR
 * Notes:
 *
 *
 */
import type { FocusCaptureSource, SectionRead } from './NoteFocusCapture.ts';

const DEFAULT_DOB = '05/29/2023';

/** Main Functions */
export function getPageInfo() {
  /*
  Description: This function retrieves the page information, including the base URL and note type,
               by parsing the HTML structure.
  Return: A dictionary with the page information
      {
          "URL": Base URL for the page,
          "Note Type": "PT Daily/SOAP Note",...
      }
  */

  const baseURL = window.location.origin;

  let noteType = null;

  // Note tabs end with a date ("PT Progress Note - 6/17/2026").
  // Patient and Clinic Navigator tabs don't, so this pattern is enough to tell them apart.
  const noteDatePattern = /^(.+) - (\d{1,2}\/\d{1,2}\/\d{4})$/;

  document.querySelectorAll('.dx-item.dx-tab.dx-tab-selected').forEach((tab) => {
    const label = (
      tab.getAttribute('data-automation') ||
      tab.querySelector('.dx-template-wrapper span')?.textContent ||
      ''
    ).trim();
    const match = label.match(noteDatePattern);
    if (match) noteType = match[1].trim();
  });

  // Fall back to alternative note type extraction from button text if available
  if (!noteType) {
    const buttonElement = document.querySelector('.t-Region-titleButton');
    if (buttonElement) {
      const parts = buttonElement.textContent.split('|');
      noteType = parts.length > 0 ? parts[0].trim() : null;
    }
  }

  return {
    URL: baseURL,
    'Note Type': noteType
  };
}

export function getPatientInfo() {
  /*
    Description: This function retrieves patient information from the document,
    including the EMR ID, first name, last name, and date of birth (DOB).
    Returns: An object with patient information
    {
        "EMR ID": emrID,
        "First Name": firstName,
        "Last Name": lastName,
        "DOB": dob,
    }
  */

  // Extract the Patient's Name and EMR ID
  const nameElement = document.querySelector(
    '.dx-tab.dx-tab-selected span'
  )?.textContent;
  let firstName = null;
  let lastName = null;
  let emrID = null;

  if (nameElement) {
    const nameText = nameElement.trim();
    const [namePart, idPart] = nameText.split('-');

    if (namePart && idPart) {
      const nameParts = namePart.trim().split(' ');
      if (nameParts.length >= 2) {
        firstName = nameParts[0];
        lastName = nameParts[nameParts.length - 1];
        emrID = idPart.trim();
      } else {
        console.warn(
          "Unable to extract patient's first and last names correctly."
        );
      }
    } else {
      console.warn('Unable to split name and EMR ID correctly.');
    }
  } else {
    console.warn('Patient name element not found.');
  }

  const dob = findBirthdate();

  return {
    'EMR ID': emrID,
    'First Name': firstName,
    'Last Name': lastName,
    DOB: dob
  };
}

function findBirthdate() {
  const elements = document.querySelectorAll('[data-automation="Vital Value"]');

  for (const element of elements) {
    if (element.textContent.includes('Birthdate:')) {
      const match = element.textContent.match(
        /Birthdate:(\d{2}\/\d{2}\/\d{4})/
      );
      if (match) {
        return match[1]; // Return the extracted birthdate
      }
    }
  }

  return DEFAULT_DOB; // Return null if no matching element is found
}

/*
questions - This is where we will store the questions
          - a list of dictionaries
     [{
        "Question": "Where was the injury?",
        "Type": "Multiple Choice",
        "Choices": ["Back", "Neck", "Shoulder"]
     },
        ...
        ]

*/
var questions = [];

/*
question Interactions - This is where we will store the interactions for each question
                        - a dictionary of dictionaries
         {
            "Where was the injury?": {
                "Type": "Multiple Choice",
                "Choices"{
                    "Back": () => {click the back button},
                    "Neck": () => {click the neck button},
                    "Shoulder": () => {click the shoulder button}
                }
            },
        },
        {
            "What is the injury?": {
                "Type": "Long Answer",
                "Input": (val) => {input the value into the input box}
        }
            ...
         }
*/

var questionInteractions = {};

function setTextareaValue(element, newValue) {
  if (!element) {
    console.error('Element not found');
    return;
  }

  // Set the new value
  element.value = newValue;

  // Create and dispatch an 'input' event to simulate typing
  const inputEvent = new Event('input', { bubbles: true });
  element.dispatchEvent(inputEvent);

  // Create and dispatch a 'change' event to simulate field change
  const changeEvent = new Event('change', { bubbles: true });
  element.dispatchEvent(changeEvent);

  // Create and dispatch a 'blur' event to simulate focus loss, often triggering a save
  const blurEvent = new Event('blur', { bubbles: true });
  element.dispatchEvent(blurEvent);
}

function typeTextareaValue(element, newValue) {
  if (!element) return;

  // Use native prototype setter so Vue/React/DevExtreme change detection sees the update
  // (direct element.value = x can be ignored by reactive wrappers)
  const setter = Object.getOwnPropertyDescriptor(
    HTMLTextAreaElement.prototype,
    'value'
  )?.set;
  const setValue = (value) => {
    if (setter) setter.call(element, value);
    else element.value = value;
  };

  element.focus();
  setValue('');
  element.dispatchEvent(
    new InputEvent('input', {
      bubbles: true,
      inputType: 'deleteContentBackward',
      data: null
    })
  );

  // Simulate per-character typing so DevExtreme's handlers fire and persist
  for (const char of String(newValue)) {
    element.dispatchEvent(
      new KeyboardEvent('keydown', { key: char, bubbles: true })
    );
    setValue((element.value || '') + char);
    element.dispatchEvent(
      new InputEvent('input', {
        bubbles: true,
        inputType: 'insertText',
        data: char
      })
    );
    element.dispatchEvent(
      new KeyboardEvent('keyup', { key: char, bubbles: true })
    );
  }

  element.dispatchEvent(new Event('change', { bubbles: true }));
}

function getClosestPreviousP(textarea) {
  let currentElement = textarea;
  let previousP = null;

  // Traverse up to 3 levels to find the previous <p> element
  for (let i = 0; i < 6; i++) {
    // Move to the previous sibling and check for <p> elements
    let sibling = currentElement.previousElementSibling;
    while (sibling) {
      if (sibling.tagName === 'P') {
        return sibling; // Found the closest <p> element
      }
      sibling = sibling.previousElementSibling;
    }
    // Move up one level in the DOM tree
    currentElement = currentElement.parentElement;
    if (!currentElement) break; // Stop if there are no more parent levels
  }

  // Return null if no previous <p> element was found within 3 levels
  return null;
}

function findHeadingRecursive(te, targetSelector, levelsUp) {
  // Base case: if levelsUp reaches 0 or te is null, stop
  if (levelsUp === 0 || !te) {
    return te?.querySelector(targetSelector);
  }
  // Recursive call, moving one level up
  return findHeadingRecursive(te.parentElement, targetSelector, levelsUp - 1);
}

function findBoldHeading(te) {
  return findHeadingRecursive(te, 'b', 5);
}

type TextQuestion = {
  key: string;
  existingNote: string;
  element: HTMLTextAreaElement | HTMLInputElement;
};

/**
 * The visible text fields of the note view, each with the key getQuestions
 * gives it. Shared by getQuestions and the Get All Notes capture so both name
 * a field identically. Read-only. `isTaken` says which keys the caller has
 * already used (the grids' keys), so a repeated label is numbered the same way.
 */
function collectTextQuestions(
  noteView: Element,
  isTaken: (key: string) => boolean
): TextQuestion[] {
  const found: TextQuestion[] = [];
  const used = new Set<string>();
  const textAreas = [
    ...noteView.querySelectorAll('textarea'),
    ...noteView.querySelectorAll('input.dx-texteditor-input')
  ] as Array<HTMLTextAreaElement | HTMLInputElement>;

  // Iterate over each textarea element
  textAreas.forEach((te) => {
    try {
      if (!te?.checkVisibility()) return;

      // Section Header
      const headingElement = noteView.querySelector('.dx-state-selected');
      const headerText = headingElement
        ? headingElement.textContent.trim()
        : 'Note';

      // Specific question label
      const labelElement = getClosestPreviousP(te);
      let key = labelElement
        ? labelElement.textContent.trim().replace(/:$/, '')
        : null;

      if (!key) {
        // Try a different mapping
        let labelElement = findBoldHeading(te);
        key = labelElement
          ? labelElement.textContent.trim().replace(/:$/, '')
          : null;
      }

      if (!key) return; // Skip if no label is found

      if (
        headerText !== 'Note' &&
        !key.toLowerCase().includes(headerText.toLowerCase())
      ) {
        key = `${headerText} - ${key}`;
      }

      // Check for duplicate keys and increment the number if necessary
      let originalKey = key;
      let counter = 2;
      while (used.has(key) || isTaken(key)) {
        key = `${originalKey} (${counter})`;
        counter++;
      }
      used.add(key);

      found.push({ key, existingNote: te.value.trim(), element: te });
    } catch (error) {
      // console.log("Error processing textarea:", te);
      throw error;
    }
  });
  return found;
}

export async function getQuestions(isWebPTUser = false, replyType = {}) {
  resetAutomationCancellation();
  questions = [];
  questionInteractions = {};

  // Parent View (When there are multiple tabs we must find the parent first)
  const noteView = document.querySelector(
    '.dx-item.dx-multiview-item.dx-item-selected:has(.dx-tabpanel-tabs)'
  );

  await getFlexQuestions();
  collectTextQuestions(
    noteView,
    (key) => key in questionInteractions
  ).forEach(({ key, existingNote, element: te }) => {
    // Set up each entry in questionInteractions
    questionInteractions[key] = {
      Type: 'Long Answer',
      Question: key,
      Key: key,
      ExistingNote: existingNote,
      Input: (val, replyType) => {
        if (isEmptyOrNull(val)) {
          return;
        }
        if (replyType === 'append') {
          setTextareaValue(te, existingNote + '\n' + val);
        } else {
          setTextareaValue(te, val);
        }
      }
    };

    // Also throw it into the questions list
    questions.push({
      Question: key,
      Key: key,
      Type: 'Long Answer',
      ExistingNote: existingNote
    });
  });

  return { questions: questions };
}

function isEmptyOrNull(value) {
  return (
    value === null || value === undefined || value === '' || value === 'None'
  );
}

/** For Treatments grid: treat placeholder values as empty so we skip that row and add new rows instead. */
function isTreatmentPlaceholderValue(value) {
  if (isEmptyOrNull(value)) return true;
  const v = String(value).trim().toLowerCase();
  return v === 'not set' || v === 'no treatments';
}

/**
 * True only for the canonical billable Treatments grid (key === 'Treatments').
 *
 * NOTE: this used to be a substring test on 'treatments', which also matched
 * unrelated grids such as "Treatments Related To Condition" (Subjective ->
 * Current Condition) and "Treatments To Consist Of" (Plan -> Treatment Plan).
 * Those grids were collapsed onto the same 'Treatments' key and pushed down the
 * billable-treatment code path (columnOffset 2, placeholder filtering, Find
 * Treatment popup, Settings dropdown), so their rows were dropped and nothing
 * was ever filled. Use looksLikeTreatmentGrid() when classifying a raw grid
 * title from the DOM.
 */
function isTreatmentsQuestion(question) {
  return normalizeTreatmentValue(question) === 'treatments';
}

/** Columns that only the billable Treatments grid has. */
const TREATMENT_GRID_MARKER_COLUMNS = [
  'status',
  'cpt',
  'settings',
  'tot. mins',
  'tot mins'
];

/**
 * Decide whether a DOM grid is the billable Treatments grid, based on its
 * toolbar title and its column headers. An exact "Treatments" title is enough;
 * otherwise we require a plain "Treatment" column plus at least one billing
 * column, which the lookalike grids above do not have.
 */
function looksLikeTreatmentGrid(rawQuestion, headers) {
  const title = normalizeTreatmentValue(rawQuestion);
  if (title === 'treatments' || title === 'treatment') return true;
  if (!title.includes('treatment')) return false;
  const normHeaders = (headers || []).map((h) => normalizeTreatmentValue(h));
  const hasTreatmentColumn = normHeaders.includes('treatment');
  const hasBillingColumn = normHeaders.some((h) =>
    TREATMENT_GRID_MARKER_COLUMNS.includes(h)
  );
  return hasTreatmentColumn && hasBillingColumn;
}

/** Lowercase, collapse whitespace, and convert hyphens to spaces for fuzzy matching. */
function normalizeTreatmentValue(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/-/g, ' ');
}

/**
 * The AI sometimes returns structure-wrapped objects (with 'Existing Value', etc.)
 * instead of plain values. Unwrap any such fields back to their raw value so the
 * answer rows contain only plain strings/nulls for comparison and writing.
 */
function normalizeTreatmentAnswerRow(row) {
  if (!row || typeof row !== 'object' || Array.isArray(row)) return row;
  return Object.fromEntries(
    Object.entries(row).map(([key, value]) => {
      if (
        value &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        ('Existing Value' in value ||
          'Description' in value ||
          'Choices' in value)
      ) {
        return [key, value['Existing Value'] ?? null];
      }
      return [key, value];
    })
  );
}

/**
 * Find the structure row whose Treatment name fuzzy-matches the given name.
 * Uses substring matching in both directions to handle partial names.
 * Returns the row key string, or null if not found.
 */
function findTreatmentRowKeyByName(structure, treatmentName) {
  if (!structure || !treatmentName) return null;
  const target = normalizeTreatmentValue(treatmentName);
  if (!target) return null;
  for (const [rowKey, row] of Object.entries(structure)) {
    const existing = row?.Treatment?.['Existing Value'];
    const existingNorm = normalizeTreatmentValue(existing);
    if (!existingNorm) continue;
    if (
      existingNorm === target ||
      existingNorm.includes(target) ||
      target.includes(existingNorm)
    ) {
      return rowKey;
    }
  }
  return null;
}

/**
 * Return the Find Treatment popup overlay element if it's currently open,
 * or null. Checks for the filter input first (fastest path), then falls
 * back to scanning all overlay-content elements for the "Find Treatment" label.
 */
function getFindTreatmentPopup() {
  const filterInput = document.querySelector('input[aria-label="Filter cell"]');
  if (filterInput) {
    const overlay = filterInput.closest('.dx-overlay-content');
    if (overlay && overlay.offsetParent != null) return overlay;
  }
  const overlays = Array.from(document.querySelectorAll('.dx-overlay-content'));
  return (
    overlays.find(
      (o) => o.offsetParent != null && o.textContent?.includes('Find Treatment')
    ) ?? null
  );
}

/**
 * Return all real data rows from the treatments grid, excluding the header
 * and the "No Treatments" placeholder row. Uses dx-data-row class first;
 * falls back to all tbody rows (minus the first) if the class hasn't been
 * applied yet by DevExtreme.
 */

/**
 * Close the Find Treatment popup by clicking its close button.
 * Falls back to dispatching Escape if no close button is found.
 * Retries up to 4 times with 80ms gaps if the popup stays open.
 */
async function closeFindTreatmentPopup() {
  const popup = getFindTreatmentPopup();
  if (!popup) return;
  const closeBtn = popup.querySelector('.dx-closebutton,[aria-label="Close"]');
  if (closeBtn)
    closeBtn.dispatchEvent(
      new MouseEvent('click', { bubbles: true, cancelable: true })
    );
  else
    document.dispatchEvent(
      new KeyboardEvent('keydown', { key: 'Escape', bubbles: true })
    );
  for (let i = 0; i < 4; i++) {
    await new Promise((r) => setTimeout(r, 80));
    if (!getFindTreatmentPopup()) return;
    document.dispatchEvent(
      new KeyboardEvent('keydown', { key: 'Escape', bubbles: true })
    );
  }
}

/**
 * Returns the root element to scope grid searches to the currently-visible note.
 * TheraOffice is a SPA whose multiview keeps multiple note views in the DOM
 * simultaneously. Without this scope, searches on `document` would match grids
 * from previously-visited notes that are still rendered but hidden.
 */
function getNoteViewRoot(): Element {
  return (
    document.querySelector(
      '.dx-item.dx-multiview-item.dx-item-selected:has(.dx-tabpanel-tabs)'
    ) || document.documentElement
  );
}

/**
 * Find the billable Treatments DevExtreme grid on the page. Matches on the
 * toolbar title plus the grid's own column headers, so lookalike grids such as
 * "Treatments Related To Condition" or "Treatments To Consist Of" are skipped.
 * Returns null if not found.
 */
function getTreatmentsGrid() {
  const root = getNoteViewRoot();
  const grids = Array.from(root.querySelectorAll('div.compact-grid'));
  for (const g of grids) {
    const titleEl =
      g.querySelector(
        '.dx-template-wrapper.dx-item-content.dx-toolbar-item-content'
      ) ||
      g.querySelector('.dx-datagrid-header-panel h5, .dx-toolbar h5') ||
      g.querySelector('.dx-template-wrapper, .dx-toolbar');
    const title = titleEl?.textContent || '';
    if (!normalizeTreatmentValue(title).includes('treatment')) continue;
    const headers = Array.from(
      g.querySelectorAll('div.dx-datagrid-text-content[role="presentation"]')
    ).map((header) => header.textContent?.trim() || '');
    if (looksLikeTreatmentGrid(title, headers)) return g;
  }
  return null;
}


// Update the Settings column for a specific treatment row in the Treatments grid.
// Opens the DevExtreme editor via dxclick, types the value, then commits like fancyEdit.
async function updateTreatmentSettingsCell({
  rowMatch,
  colIndex,
  value,
  rowSelector = 'tr.dx-data-row'
}) {
  const grid = getTreatmentsGrid();
  const rows = Array.from((grid || document).querySelectorAll(rowSelector));
  const row = rows.find(rowMatch);
  if (!row) {
    console.warn('updateTreatmentSettingsCell: row not found', value);
    return null;
  }
  let cell = row.querySelector(`td[aria-colindex="${colIndex}"]`);
  if (!cell) {
    console.warn(
      'updateTreatmentSettingsCell: cell not found',
      colIndex,
      value
    );
    return null;
  }
  const nextValue = String(value);

  if (grid) (grid as HTMLElement).click();

  let textarea = null;
  const timeout = 500;
  const startTime = Date.now();
  while (!textarea && Date.now() - startTime < timeout) {
    if (isAutomationCancellationRequested()) return null;
    cell.dispatchEvent(new Event('dxclick', { bubbles: true }));
    const freshRow = Array.from(
      (grid || document).querySelectorAll(rowSelector)
    ).find(rowMatch);
    if (freshRow) {
      cell = freshRow.querySelector(`td[aria-colindex="${colIndex}"]`) || cell;
    }
    textarea = await querySelectorWait(cell, 'textarea', 50);
  }

  if (!textarea || isAutomationCancellationRequested()) return null;

  typeTextareaValue(textarea, nextValue);
  textarea.dispatchEvent(
    new Event('blur', { bubbles: true, cancelable: true })
  );
  if (grid) (grid as HTMLElement).click();
  return cell;
}

/**
 * Scan all `div.compact-grid` elements on the page and build a nested structure
 * object describing each grid's rows and editable columns. Each field entry
 * contains the current value and an `Input` closure that writes a new value.
 *
 * Two code paths exist depending on the grid type:
 *  - Treatment grid: columnOffset=2, placeholder-row filtering, extended selector
 *  - All other grids: columnOffset=1, Area-column empty check, textarea-only selector
 */
function generateStructureFromDocument(quiet = false, visibleOnly = false) {
  const structure = {};

  // Find all grids within the currently-visible note view only.
  const grids = getNoteViewRoot().querySelectorAll('div.compact-grid');

  grids.forEach((grid) => {
    // The note view also holds the other open notes' grids, hidden.
    if (visibleOnly && !(grid as any).checkVisibility?.()) return;
    const rows = grid.querySelectorAll('tbody > tr');

    // Try compound-class selector first (all three classes on same element, as
    // rendered by TheraOffice's Vue component), then fall back to the legacy
    // descendant selector, then an h5 heading inside the toolbar area.
    const questionElement =
      grid.querySelector(
        '.dx-template-wrapper.dx-item-content.dx-toolbar-item-content'
      ) ||
      grid.querySelector(
        '.dx-template-wrapper .dx-item-content .dx-toolbar-item-content'
      ) ||
      grid.querySelector('.dx-datagrid-header-panel h5, .dx-toolbar h5');
    const rawQuestion =
      questionElement?.textContent.trim() || 'Default Question';

    // Extract headers dynamically (needed before we can classify the grid).
    const rawHeaders = Array.from(
      grid.querySelectorAll('div.dx-datagrid-text-content[role="presentation"]')
    )
      .map((header) => header.textContent.trim())
      .filter(Boolean);

    // Only the real billable Treatments grid gets the treatment code path.
    // Lookalike titles ("Treatments Related To Condition", "Treatments To
    // Consist Of", ...) keep their own key and go through the generic grid path.
    const isTreatmentGrid = looksLikeTreatmentGrid(rawQuestion, rawHeaders);
    const question = isTreatmentGrid ? 'Treatments' : rawQuestion;
    const key = question;

    const headers = rawHeaders.filter(
      (header) => !isTreatmentGrid || (header !== 'HEP' && header !== 'P')
    );

    if (!question || headers.length === 0) {
      if (!quiet) {
        console.warn('No valid question/key or headers found in the grid.');
      }
      return;
    }

    if (!structure[key]) {
      structure[key] = {
        Question: question,
        Headers: headers,
        Structure: isTreatmentsQuestion(question) ? [] : {}
      };
    }

    if (isTreatmentsQuestion(question)) {
      // ── TREATMENT GRID ─────────────────────────────────────────────────────
      // columnOffset=2 skips the two leading action columns in the treatment grid.
      // Structure is an array so the AI matches by position (same as WebPT goals).
      const columnOffset = 2;

      rows.forEach((row, rowIndex) => {
        if (rowIndex === 0) return; // Skip the header row
        const domRowIndex = rowIndex; // 1-based; used directly in fancyEdit
        const columns = Array.from(row.querySelectorAll('td'));

        // Skip placeholder / "Not Set" rows.
        const statusValue =
          columns[2]?.querySelector('textarea')?.value.trim() ||
          columns[2]?.textContent.trim() ||
          '';
        const cptValue =
          columns[3]?.querySelector('textarea')?.value.trim() ||
          columns[3]?.textContent.trim() ||
          '';
        const treatmentValue =
          columns[4]?.querySelector('textarea')?.value.trim() ||
          columns[4]?.textContent.trim() ||
          '';
        const settingsValue =
          columns[5]?.querySelector('textarea')?.value.trim() ||
          columns[5]?.textContent.trim() ||
          '';

        if (
          isTreatmentPlaceholderValue(statusValue) &&
          isTreatmentPlaceholderValue(cptValue) &&
          isTreatmentPlaceholderValue(treatmentValue) &&
          isTreatmentPlaceholderValue(settingsValue)
        ) {
          return;
        }

        // Each non-placeholder row becomes one array entry.
        const rowData = {};

        headers.forEach((header, index) => {
          // columnOffset=2 skips the two leading action columns in the treatment grid
          const columnIndex = index + columnOffset;

          // Prefer the textarea value (edit mode) over textContent (read mode)
          const te = columns[columnIndex]?.querySelector('textarea');
          const value =
            te?.value.trim() || columns[columnIndex]?.textContent.trim() || '';

          rowData[header] = {
            'Existing Value': value,
            // Input is called by the AI answer applier with the new value
            Input: async (val, replyType) => {
              // Settings can be legitimately absent — skip rather than clear
              if (header === 'Settings' && (val == null || val === '')) return;
              if (!val) return;

              // Treatment cells use DevExtreme inputs, not plain textareas,
              // so we need the extended selector to find the active editor
              const treatmentSelector =
                'textarea, input.dx-texteditor-input, input:not([type="hidden"])';
              if (replyType === 'append') {
                return fancyEdit(
                  grid,
                  domRowIndex,
                  columnIndex,
                  `${value}\n${val}`,
                  treatmentSelector
                );
              }

              // Settings cell requires a special updater because DevExtreme
              // renders it as a dropdown/overlay rather than an inline editor
              if (header === 'Settings') {
                return updateTreatmentSettingsCell({
                  rowMatch: (tr) =>
                    (tr.innerText || '').includes(treatmentValue),
                  colIndex: 6, // Settings column aria-colindex confirmed in TheraOffice
                  value: val
                });
              }

              return fancyEdit(
                grid,
                domRowIndex,
                columnIndex,
                val,
                treatmentSelector
              );
            }
          };
        });

        (structure[key].Structure as any[]).push(rowData);
      });
    } else {
      // ── ALL OTHER GRIDS: original logic ───────────────────────────────────
      // columnOffset of 1, checks column[1] for emptiness, uses rowIndex+1
      // in fancyEdit to account for the header row.
      rows.forEach((row, rowIndex) => {
        if (rowIndex === 0) return; // Skip the header row
        rowIndex--; // Adjust the row index to start from 0
        const columns = Array.from(row.querySelectorAll('td'));

        // Skip DevExtreme freespace/spacer rows — they are layout artefacts, not data.
        // We intentionally keep rows whose Area column is empty so that the API
        // receives the full column structure even before the user has typed anything.
        if (row.classList.contains('dx-freespace-row')) {
          return;
        }

        if (!structure[key].Structure[rowIndex]) {
          structure[key].Structure[rowIndex] = {};
        }

        headers.slice(0).forEach((header, index) => {
          const columnIndex = index + 1; // Store column index for later lookup
          const te = columns[columnIndex]?.querySelector('textarea'); // Try to locate the textarea immediately
          const value =
            te?.value.trim() || columns[columnIndex]?.textContent.trim() || '';

          structure[key].Structure[rowIndex][header] = {
            Type: 'string',
            'Existing Value': value,
            Input: async (val, replyType) => {
              if (!val) {
                return;
              }
              console.log(
                `Column Index: ${columnIndex}, Row Index: ${rowIndex}, Value: ${val}`
              );

              // Plain DevExtreme grid cells render input.dx-texteditor-input (not
              // textarea) when clicked into edit mode. DropDownBox cells render a
              // visible textarea alongside a hidden input — querySelectorWait will
              // skip the hidden one and find the visible textarea thanks to the
              // checkVisibility filter, so this wider selector is safe for both.
              const cellSelector = 'textarea, input.dx-texteditor-input';

              // Update the value based on the reply type
              if (replyType === 'append') {
                // rowIndex+1 to account for the header row
                return fancyEdit(
                  grid,
                  rowIndex + 1,
                  columnIndex,
                  `${value}\n${val}`,
                  cellSelector
                );
              } else {
                return fancyEdit(
                  grid,
                  rowIndex + 1,
                  columnIndex,
                  val,
                  cellSelector
                );
              }
            }
          };
        });
      });
    }
  });

  return structure;
}

function getC(rN, cN, grid) {
  // We are doing dom manipulation here and often time things get re-rendered
  // So we need a helper function to get the element after a re-render
  const rows = grid.querySelectorAll('tbody > tr');
  const row = rows[rN];
  const cells = Array.from(row.querySelectorAll('td'));
  const cell = cells[cN];
  return cell;
}

async function querySelectorWait(target, selector, timeout = 50, interval = 2) {
  // Retry interval in milliseconds
  const startTime = Date.now();
  while (Date.now() - startTime < timeout) {
    if (isAutomationCancellationRequested()) return null;
    const elements = Array.from(target.querySelectorAll(selector));
    // Prefer visible elements. DevExtreme DropDownBox templates render a hidden
    // input.dx-texteditor-input alongside the real visible textarea — we must
    // skip it or we'll write into an invisible element and nothing sticks.
    const element =
      elements.find((el) => {
        try {
          return typeof (el as any).checkVisibility === 'function'
            ? (el as any).checkVisibility()
            : (el as HTMLElement).offsetParent !== null;
        } catch {
          return true;
        }
      }) ?? null;
    if (element) return element;
    if (!(await sleepWithAutomationCancel(interval))) return null;
  }
  return null; // Return null if timeout is exceeded
}

async function fancyEdit(grid, rN, cN, value, selector = 'textarea') {
  if (isAutomationCancellationRequested()) return null;
  grid.click(); // Clear any existing focus
  // There is a slight chance we don't get the text area on the first try
  let textArea = null;
  const timeout = 500;
  const startTime = Date.now();
  while (!textArea && Date.now() - startTime < timeout) {
    if (isAutomationCancellationRequested()) return null;
    let cell = getC(rN, cN, grid);
    cell.dispatchEvent(new Event('dxclick', { bubbles: true }));
    cell = getC(rN, cN, grid);
    // I know that it will show within 30ms...(Trial and error)
    textArea = await querySelectorWait(cell, selector, 50);
  }

  if (isAutomationCancellationRequested() || !textArea) return null;

  // This will bomb intentionally if we didn't find the text area
  if (
    textArea instanceof HTMLInputElement &&
    (textArea.type === 'checkbox' || textArea.type === 'radio')
  ) {
    textArea.checked = /^(yes|true|1)$/i.test(String(value || '').trim());
  } else {
    textArea.value = value;
  }
  textArea.dispatchEvent(
    new Event('keydown', { bubbles: true, cancelable: true })
  );
  textArea.dispatchEvent(
    new Event('keypress', { bubbles: true, cancelable: true })
  );
  textArea.dispatchEvent(
    new Event('keyup', { bubbles: true, cancelable: true })
  );
  textArea.dispatchEvent(
    new Event('input', { bubbles: true, cancelable: true })
  );
  textArea.dispatchEvent(
    new Event('change', { bubbles: true, cancelable: true })
  );
  textArea.dispatchEvent(
    new Event('blur', { bubbles: true, cancelable: true })
  );
  grid.click(); // Clear any existing focus
  return;
}

/**
 * The grids of the visible note as getQuestions reports them: the structure the
 * write-back keeps, and the one sent to the API. Shared with the Get All Notes
 * capture. Read-only (it builds closures but never calls them).
 */
function flexEntriesFromDocument(quiet = false, visibleOnly = false) {
  const structure = generateStructureFromDocument(quiet, visibleOnly);

  return Object.keys(structure).map((key) => {
    // API rejects Flex questions with empty structure.
    // Keep every grid visible and valid even before rows are created.
    if (Object.keys(structure[key]?.Structure || {}).length === 0) {
      const headers = structure[key]?.Headers || [];
      const placeholder = {};
      headers.forEach((header) => {
        placeholder[header] = {
          'Existing Value': '',
          Input: async () => null
        };
      });
      // Treatments keeps an array (prototype row, no numeric keys in the payload);
      // other grids keep the row-index-keyed object shape the write-back expects.
      structure[key].Structure = isTreatmentsQuestion(key)
        ? [placeholder]
        : { 0: placeholder };
    }

    // Treatments: send as indexed dict so the backend treats it as a fixed structure
    // (no dynamic expansion). The fill AI will only update Settings, Tot. Mins., CPT,
    // and Status for whatever rows are already in the EMR — it won't add new rows.
    const structureForApi =
      isTreatmentsQuestion(key) && Array.isArray(structure[key]?.Structure)
        ? Object.fromEntries(
            (structure[key].Structure as any[]).map((row, i) => [
              row?.Treatment?.['Existing Value'] || String(i),
              row
            ])
          )
        : structure[key]?.Structure;

    return {
      key,
      question: structure[key]?.Question,
      structure: structure[key]?.Structure,
      structureForApi
    };
  });
}

function getFlexQuestions() {
  flexEntriesFromDocument().forEach(
    ({ key, question, structure, structureForApi }) => {
      // questionInteractions keeps the original array so the write-back can use .map/.findIndex.
      // questions sends the dict so the backend skips dynamic expansion for treatments.
      questionInteractions[key] = {
        Type: 'Flex',
        Question: question,
        Structure: structure
      };

      questions.push({
        Question: question,
        Key: key,
        Type: 'Flex',
        Structure: structureForApi
      });
    }
  );
}

export async function answerQuestions(answers) {
  /*
    Description: This function will take the answers and populate the questions

    Args:
        answers: A dictionary of answers
            "Question": "The question in question"
            "Answer": "The answer to the question"

    Return: A list of any questions that were not answered, perhaps the answer didn't match the questions expected or whatnot...
        [
            {
                Question: "Where was the injury?",
                Answer: "Back"
                Error: "The error message" (Choice not found, etc)
            },
            ...
        ]

    */
  const errors = [];
  resetAutomationCancellation();

  beginOverlay('Answering questions…', { title: 'ComprehendPT' });
  try {
    for (const answer of answers) {
      if (isAutomationCancellationRequested()) break;
      const error = await answerQuestion(
        answer['Question'],
        answer['Answer'],
        answer['Reply Type'],
        true
      );
      if (isAutomationCancellationRequested()) break;
      if (error) {
        errors.push({
          Question: answer['Question'],
          Answer: answer['Answer'],
          Error: error
        });
      }
    }

    console.error('Errors:');
    console.error(errors);
    return errors;
  } finally {
    endOverlay();
  }
}

export async function answerQuestion(
  question,
  answer,
  replyType,
  preserveCancellationState = false
) {
  /*
    Description: This function will take the answer and populate the question

    Args:
        question: The question in question
        answer: The answer to the question

    Return: null if success otherwise a string with the error message
    */

  if (!preserveCancellationState) {
    resetAutomationCancellation();
  }

  beginOverlay('Answering questions…', { title: 'ComprehendPT' });
  try {
    if (isAutomationCancellationRequested()) return USER_CANCELLED_MESSAGE;
    // Make sure the question exists
    if (questionInteractions[question] == null) {
      console.error('Question not found');
      console.error(question);

      return 'Question not found';
    }

    // If it's a flex do so
    if (questionInteractions[question]['Type'] === 'Flex') {
      return await answerFlexQuestion(question, answer, replyType);
    }

    // If it is a Long Answer or long answer, just populate the input
    if (questionInteractions[question]['Input']) {
      if (isAutomationCancellationRequested()) return USER_CANCELLED_MESSAGE;
      await questionInteractions[question]['Input'](answer, replyType);
      if (isAutomationCancellationRequested()) return USER_CANCELLED_MESSAGE;
      return true;
    }

    // Check if the answer is a list of answers or just a single answer
    if (Array.isArray(answer)) {
      // Choose All
      var notFoundAnswers = [];
      for (var i = 0; i < answer.length; i++) {
        if (answer[i] in questionInteractions[question]['Choices']) {
          questionInteractions[question]['Choices'][answer[i]](true);
        } else {
          notFoundAnswers.push(answer[i]);
        }
      }
      if (notFoundAnswers.length > 0) {
        console.error(
          'The following answers were not found: ' + notFoundAnswers
        );
        return 'The following answers were not found: ' + notFoundAnswers;
      } else {
        return true;
      }
    } else {
      // Multiple Choice
      if (answer in questionInteractions[question]['Choices']) {
        questionInteractions[question]['Choices'][answer]();
        return true;
      } else {
        return 'Answer not found';
      }
    }
  } finally {
    endOverlay();
  }
}

/**
 * Apply AI answers to a Flex (grid) question. For treatment questions:
 *   1. Normalize and dedupe the answer rows
 *   2. Add any missing treatment rows via the Find Treatment popup
 *   3. Poll until the DOM structure reflects the expected row count
 *   4. Write column values using the Input closures from the structure
 *   5. Re-apply Settings in a final pass (see final-pass comment below)
 *
 * For non-treatment grids, skips the popup logic and writes columns directly.
 */
async function answerFlexQuestion(question, answer, replyType = 'overwrite') {
  try {
    if (isAutomationCancellationRequested()) return USER_CANCELLED_MESSAGE;
    const errors = [];
    const questionObject = questionInteractions[question];

    if (!questionObject) {
      console.error(`Question with Key "${question}" not found.`);
      return {
        Question: question,
        Error: `Question with Key "${question}" not found.`
      };
    }

    // Treatments: add any new rows via Find popup, then fill positionally (same pattern as WebPT goals).
    if (
      isTreatmentsQuestion(question) &&
      answer &&
      typeof answer === 'object'
    ) {
      let structure = (questionObject.Structure as any[]) || [];

      // Unwrap { answer: [...] } wrapper if present, then normalise to array
      let rows: any[] =
        Array.isArray(answer.answer) && !('Treatment' in answer)
          ? answer.answer
          : Array.isArray(answer)
            ? answer
            : Object.values(answer);
      rows = rows
        .map((r) =>
          r && typeof r === 'object' ? normalizeTreatmentAnswerRow(r) : null
        )
        .filter(Boolean);

      // Never add treatments via the Find popup — only fill values for rows already in the grid.

      // Helper: find the structure entry whose Treatment name best matches an AI treatment name.
      // Falls back to positional index so existing behaviour is preserved when names don't match.
      const findStructureEntryForRow = (
        aiTreatmentName: string,
        fallbackIndex: number
      ) => {
        if (aiTreatmentName) {
          const target = normalizeTreatmentValue(aiTreatmentName);
          const matchedIdx = structure.findIndex((s) => {
            const existing = normalizeTreatmentValue(
              s?.Treatment?.['Existing Value'] || ''
            );
            return (
              existing &&
              (existing === target ||
                existing.includes(target) ||
                target.includes(existing))
            );
          });
          if (matchedIdx >= 0) return structure[matchedIdx];
        }
        return structure[fallbackIndex];
      };

      // Name-based fill: match each answer row to its DOM structure row by treatment name.
      // This prevents the positional mismatch that occurs when the DOM order differs from
      // the AI answer order (e.g. after new rows are appended via the Find popup).
      for (let i = 0; i < rows.length; i++) {
        if (isAutomationCancellationRequested()) return USER_CANCELLED_MESSAGE;
        const rowValues = rows[i];
        const field = findStructureEntryForRow(rowValues['Treatment'], i);
        if (!field) continue;
        for (const [subFieldKey, value] of Object.entries(rowValues)) {
          if (isAutomationCancellationRequested())
            return USER_CANCELLED_MESSAGE;
          // Treatment name is set by the Find popup; HEP/P are section markers not columns
          if (
            subFieldKey === 'Treatment' ||
            subFieldKey === 'HEP' ||
            subFieldKey === 'P'
          )
            continue;
          if (field[subFieldKey]?.Input) {
            try {
              await field[subFieldKey].Input(value, replyType);
            } catch (err) {
              errors.push({
                Field: `${i}.${subFieldKey}`,
                Error: `Error applying value: ${(err as any).message}`
              });
            }
          }
        }
      }

      // Re-apply Settings after all other fields (DevExtreme can reset it on re-render).
      // Use the same name-based match so Settings land on the correct row.
      if (!(await sleepWithAutomationCancel(400)))
        return USER_CANCELLED_MESSAGE;
      for (let i = 0; i < rows.length; i++) {
        if (isAutomationCancellationRequested()) return USER_CANCELLED_MESSAGE;
        const settingsValue = rows[i]['Settings'];
        const treatmentLabel = rows[i]['Treatment'];
        if (!settingsValue || !treatmentLabel) continue;
        // Use the DOM-read treatment name from the matched structure entry so the
        // rowMatch selector finds the right grid row even when the AI label differs slightly.
        const matchedEntry = findStructureEntryForRow(treatmentLabel, i);
        const matchLabel = normalizeTreatmentValue(
          matchedEntry?.Treatment?.['Existing Value'] || treatmentLabel
        );
        await updateTreatmentSettingsCell({
          rowMatch: (tr) => {
            const rowNorm = normalizeTreatmentValue(tr.innerText || '');
            return rowNorm.includes(matchLabel) || matchLabel.includes(rowNorm);
          },
          colIndex: 6,
          value: settingsValue
        });
      }

      if (errors.length > 0) {
        console.error('Errors occurred:', errors);
        return errors;
      }
      return null;
    }

    const structure = questionObject.Structure || {};
    const structureKeys = Object.keys(structure);

    /** Do the answering sequentially */
    for (const [fieldKey, fieldDetails] of Object.entries(answer)) {
      if (isAutomationCancellationRequested()) return USER_CANCELLED_MESSAGE;
      if (!fieldDetails || typeof fieldDetails !== 'object') continue;
      let resolvedFieldKey = fieldKey;
      let field = structure[fieldKey];
      if (isTreatmentsQuestion(question)) {
        const treatmentName = fieldDetails['Treatment'];
        const matchedRowKey = findTreatmentRowKeyByName(
          structure,
          treatmentName
        );
        if (matchedRowKey == null) {
          errors.push({
            Field: `${fieldKey}.Treatment`,
            Error: `Could not find matching treatment row for "${treatmentName}"`
          });
          continue;
        }
        resolvedFieldKey = matchedRowKey;
        field = structure[resolvedFieldKey];
      } else if (!field && /^\d+$/.test(String(fieldKey))) {
        // Fallback for numeric row keys when DOM indexing and answer indexing differ.
        field = structure[structureKeys[Number(fieldKey)]] || structure;
      }

      if (!field) {
        console.error(`Field "${fieldKey}" not found in the Structure.`);
        errors.push({ Field: fieldKey, Error: 'Field not found in Structure' });
        continue;
      }

      for (const [subFieldKey, value] of Object.entries(fieldDetails)) {
        if (isAutomationCancellationRequested()) return USER_CANCELLED_MESSAGE;
        // Treatment is set by the Find popup when we add the row; skip writing it again.
        if (isTreatmentsQuestion(question) && subFieldKey === 'Treatment') {
          continue;
        }
        if (
          isTreatmentsQuestion(question) &&
          (subFieldKey === 'HEP' || subFieldKey === 'P')
        ) {
          continue;
        }
        if (field[subFieldKey]?.Input) {
          try {
            await field[subFieldKey].Input(value, replyType);
          } catch (error) {
            errors.push({
              Field: `${resolvedFieldKey}.${subFieldKey}`,
              Error: `Error applying value: ${error.message}`
            });
          }
        } else {
          console.warn(
            `Input function not found for "${resolvedFieldKey}.${subFieldKey}"`
          );
          errors.push({
            Field: `${resolvedFieldKey}.${subFieldKey}`,
            Error: 'Input function not found'
          });
        }
      }
    }

    // Final pass: re-apply Settings for every treatment row.
    // The Settings cell uses a DevExtreme overlay editor that can be dismissed
    // or overwritten by subsequent fancyEdit calls on other columns. Running
    // this after all other fields are written ensures the final DOM value
    // matches the AI answer. It runs twice with a delay between passes because
    // TheraOffice sometimes re-renders the row after the first write.
    if (isTreatmentsQuestion(question)) {
      // Wait for any pending DevExtreme animations/re-renders to settle
      if (!(await sleepWithAutomationCancel(400))) {
        return USER_CANCELLED_MESSAGE;
      }
      // First Settings pass
      for (const details of Object.values(answer)) {
        if (isAutomationCancellationRequested()) return USER_CANCELLED_MESSAGE;
        if (!details || typeof details !== 'object') continue;
        const treatmentLabel = details['Treatment'];
        const settingsValue = details['Settings'];
        if (!treatmentLabel || settingsValue == null || settingsValue === '')
          continue;
        await updateTreatmentSettingsCell({
          rowMatch: (tr) => (tr.innerText || '').includes(treatmentLabel),
          colIndex: 6,
          value: settingsValue
        });
      }
      // Short pause before the confirmation pass
      if (!(await sleepWithAutomationCancel(250))) {
        return USER_CANCELLED_MESSAGE;
      }
      // Second Settings pass — overwrites any value that got reset by re-render
      for (const details of Object.values(answer)) {
        if (isAutomationCancellationRequested()) return USER_CANCELLED_MESSAGE;
        if (!details || typeof details !== 'object') continue;
        const treatmentLabel = details['Treatment'];
        const settingsValue = details['Settings'];
        if (!treatmentLabel || settingsValue == null || settingsValue === '')
          continue;
        await updateTreatmentSettingsCell({
          rowMatch: (tr) => (tr.innerText || '').includes(treatmentLabel),
          colIndex: 6,
          value: settingsValue
        });
      }
    }

    /** Parse the errors */
    if (errors.length > 0) {
      console.error('Errors occurred:', errors);
      return errors;
    }

    return null; // Success
  } catch (error) {
    console.error('Unexpected error:', error);
    return {
      Question: question,
      Error: `Unexpected error: ${error.message}`
    };
  }
}

/* Start the Blur Overlay */
const OVERLAY_ID = 'TheraOfficeBlurOverlay';
const ACTIVE_CLASS = 'theraoffice-blur-overlay-active';
const CSS_ID = 'TheraOfficeBlurOverlayStyles';

let overlayEl, msgEl, closeBtn;
let usingBackdrop = true;
let lastFocused = null;
let overlayRefCount = 0;
let automationCancelRequested = false;

const USER_CANCELLED_MESSAGE = 'Cancelled by user.';

/** Clear the cancellation flag before starting a new automation run. */
function resetAutomationCancellation() {
  automationCancelRequested = false;
}

/** Signal all in-progress automation loops to stop at their next check. */
function requestAutomationCancellation() {
  automationCancelRequested = true;
}

/** Returns true if the user has requested cancellation. */
function isAutomationCancellationRequested() {
  return automationCancelRequested;
}

/**
 * Sleep for `ms` milliseconds, but bail out immediately if cancellation
 * is requested. Returns false if cancelled, true if the sleep completed.
 */
async function sleepWithAutomationCancel(ms) {
  if (isAutomationCancellationRequested()) return false;
  await new Promise((resolve) => setTimeout(resolve, ms));
  return !isAutomationCancellationRequested();
}

/** Inject the overlay's CSS once per page load; no-op if already present. */
function injectOverlayStyles() {
  if (document.getElementById(CSS_ID)) return;
  const style = document.createElement('style');
  style.id = CSS_ID;
  style.textContent = `
    html.${ACTIVE_CLASS} { overflow: hidden; }
    body.${ACTIVE_CLASS} { overflow: hidden; }

    html.${ACTIVE_CLASS} body > *:not(#${OVERLAY_ID}) {
      filter: blur(var(--blur-overlay-amount, 8px)) saturate(0.9);
      pointer-events: none;
      user-select: none;
    }

    #${OVERLAY_ID} {
      position: fixed;
      inset: 0;
      --brand-color: rgb(107, 216, 136);
      z-index: 2147483647;
      display: none;
      align-items: center;
      justify-content: center;
      padding: 24px;
    }

    #${OVERLAY_ID}[data-open="true"] { display: flex; }

    #${OVERLAY_ID}::before {
      content: "";
      position: absolute;
      inset: 0;
      background: rgba(0, 0, 0, 0.25);
      backdrop-filter: blur(var(--blur-overlay-amount, 8px)) brightness(0.95);
      -webkit-backdrop-filter: blur(var(--blur-overlay-amount, 8px)) brightness(0.95);
    }

    #${OVERLAY_ID} .blur-msg {
      position: relative;
      max-width: min(720px, 90vw);
      width: max(320px, 40vw);
      background: rgba(255, 255, 255, 0.9);
      border-radius: 16px;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
      padding: 20px 24px;
      font-family: system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif;
      color: #111;
      backdrop-filter: blur(2px);
    }

    #${OVERLAY_ID} .blur-msg h2 {
      margin: 0 0 10px 0;
      font-size: 18px;
      font-weight: 600;
      color: var(--brand-color);
    }

    #${OVERLAY_ID} .blur-msg .blur-text {
      line-height: 1.5;
      font-size: 15px;
      white-space: pre-wrap;
    }

    #${OVERLAY_ID} .blur-close {
      position: fixed;
      top: 12px;
      right: 12px;
      appearance: none;
      border: none;
      background: var(--brand-color);
      color: #fff;
      width: 36px;
      height: 36px;
      border-radius: 10px;
      font-size: 20px;
      line-height: 36px;
      text-align: center;
      cursor: pointer;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
    }

    #${OVERLAY_ID} .blur-close:hover { background: #00937f; }
    #${OVERLAY_ID} .blur-close:focus { outline: 2px solid #fff; outline-offset: 2px; }

  `;
  document.head.appendChild(style);
}

/** Create the close button if it doesn't exist yet and attach it to the overlay. */
function ensureOverlayCloseButton() {
  closeBtn = overlayEl.querySelector('.blur-close');
  if (closeBtn) return closeBtn;

  closeBtn = document.createElement('button');
  closeBtn.className = 'blur-close';
  closeBtn.setAttribute('type', 'button');
  closeBtn.setAttribute('aria-label', 'Close overlay');
  closeBtn.textContent = 'x';
  closeBtn.addEventListener('click', () =>
    hideOverlay({ cancelActiveWork: true })
  );
  overlayEl.appendChild(closeBtn);
  return closeBtn;
}

/**
 * Bind Escape-key and backdrop-click dismissal to the overlay (once only).
 * Clicking inside the card does NOT dismiss — only the close button or Escape.
 */
function bindOverlayDismissHandlers(card) {
  if (overlayEl.dataset.dismissBound === 'true') return;

  overlayEl.addEventListener('keydown', (e) => {
    if (overlayEl.dataset.open === 'true' && e.key === 'Escape') {
      hideOverlay({ cancelActiveWork: true });
    }
  });

  overlayEl.addEventListener('click', (e) => {
    const clickedInsideCard = e.target === card || card.contains(e.target);
    if (!clickedInsideCard) {
      return;
    }
  });

  overlayEl.dataset.dismissBound = 'true';
}

/**
 * Build and mount the overlay DOM if it doesn't exist; otherwise re-attach
 * module refs to the existing DOM elements (handles page re-renders).
 */
function createOverlay() {
  if (document.getElementById(OVERLAY_ID)) {
    overlayEl = document.getElementById(OVERLAY_ID);
    msgEl = overlayEl.querySelector('.blur-text');
    const card = overlayEl.querySelector('.blur-msg');
    ensureOverlayCloseButton();
    if (card) bindOverlayDismissHandlers(card);
    return;
  }

  overlayEl = document.createElement('div');
  overlayEl.id = OVERLAY_ID;
  overlayEl.setAttribute('aria-hidden', 'true');

  const card = document.createElement('div');
  card.className = 'blur-msg';
  card.setAttribute('role', 'dialog');
  card.setAttribute('aria-modal', 'true');
  card.setAttribute('aria-live', 'polite');
  card.setAttribute('tabindex', '-1');

  const title = document.createElement('h2');
  title.textContent = 'ComprehendPT';

  msgEl = document.createElement('div');
  msgEl.className = 'blur-text';
  msgEl.textContent = '';

  card.appendChild(title);
  card.appendChild(msgEl);

  overlayEl.appendChild(card);
  ensureOverlayCloseButton();
  document.body.appendChild(overlayEl);

  bindOverlayDismissHandlers(card);
}

/** Detect whether the browser supports CSS backdrop-filter for the blur effect. */
function ensureOverlaySupport() {
  usingBackdrop =
    CSS.supports('backdrop-filter: blur(8px)') ||
    CSS.supports('-webkit-backdrop-filter: blur(8px)');
}

/**
 * Show the blur overlay with a message. Creates/injects styles if needed.
 * Saves the currently focused element so focus can be restored on hide.
 */
function showOverlay(
  message = '',
  opts: {
    title?: string;
    blurAmount?: number;
    textColor?: string;
    background?: string;
    width?: string;
  } = {}
) {
  injectOverlayStyles();
  createOverlay();
  ensureOverlaySupport();

  const {
    title = 'ComprehendPT',
    blurAmount = 8,
    textColor,
    background,
    width
  } = opts;

  overlayEl.style.setProperty('--blur-overlay-amount', String(blurAmount));
  const card = overlayEl.querySelector('.blur-msg');
  if (textColor) card.style.color = textColor;
  if (background) card.style.background = background;
  if (width) card.style.width = width;

  overlayEl.querySelector('h2').textContent = title;
  msgEl.textContent = message ?? '';

  document.documentElement.classList.add(ACTIVE_CLASS);
  document.body.classList.add(ACTIVE_CLASS);

  if (!usingBackdrop) {
    // Fallback blur is handled by the ACTIVE_CLASS CSS rule.
  }

  closeBtn.style.display = '';
  overlayEl.dataset.open = 'true';
  overlayEl.setAttribute('aria-hidden', 'false');

  lastFocused =
    document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
  closeBtn?.focus();

  return true;
}

/** Update the overlay message text while it's already visible. */
function updateOverlay(message = '') {
  if (!overlayEl) createOverlay();
  msgEl.textContent = message ?? '';
  return true;
}

/**
 * Hide the overlay and restore focus to the previously active element.
 * If `cancelActiveWork` is true, also sets the cancellation flag and resets
 * the ref-count so any in-progress automation stops immediately.
 */
function hideOverlay(opts: { cancelActiveWork?: boolean } = {}) {
  if (!overlayEl) return false;
  const { cancelActiveWork = false } = opts;

  if (cancelActiveWork) {
    requestAutomationCancellation();
    overlayRefCount = 0;
    void closeFindTreatmentPopup();
  }

  overlayEl.dataset.open = 'false';
  overlayEl.setAttribute('aria-hidden', 'true');
  document.documentElement.classList.remove(ACTIVE_CLASS);
  document.body.classList.remove(ACTIVE_CLASS);

  if (lastFocused && document.contains(lastFocused)) {
    try {
      lastFocused.focus();
    } catch {}
  }
  lastFocused = null;
  return true;
}

/** Returns true if the overlay is currently open and visible. */
function isOverlayVisible() {
  return !!(overlayEl && overlayEl.dataset.open === 'true');
}

/**
 * Reference-counted overlay open. Multiple callers can call beginOverlay/endOverlay
 * and the overlay stays visible until all callers have called endOverlay.
 * Shows the overlay on the first call; subsequent calls just update the message.
 */
function beginOverlay(
  message,
  opts: {
    title?: string;
    blurAmount?: number;
    textColor?: string;
    background?: string;
    width?: string;
  } = {}
) {
  overlayRefCount++;
  if (!isOverlayVisible()) {
    showOverlay(message, opts);
  } else {
    updateOverlay(message);
  }
  return overlayRefCount;
}

/**
 * Decrement the overlay ref-count. Hides the overlay when the count
 * reaches zero (i.e., all callers are done).
 */
function endOverlay() {
  overlayRefCount = Math.max(0, overlayRefCount - 1);
  if (overlayRefCount === 0 && isOverlayVisible()) {
    hideOverlay();
  }
}
/* End the Blur Overlay */

/*
Get All Notes for TheraOffice is event-driven (NoteFocusCapture.ts), not
polling. It has its own export on purpose: the standard *ForCapture names would
also start NoteCapture's poll.

The chart is a DevExtreme multiview: one item per open patient, holding a tab
strip (Patient Navigator + the open notes) and, per note, a section tree whose
selected node is the only content rendered. So a read sees whatever the
clinician has opened, and only that. Everything unopened is absent, never empty:
NoteStore merges one top-level key at a time and keeps the earlier value of a
key a read does not return.

The whole visible note is one capture "section". Reading by tree node would
need the node's name when an idle callback runs, which can be after the
clinician has moved on; one section cannot miss that way. Values are read by the
very extractors getQuestions uses (collectTextQuestions, flexEntriesFromDocument), so a
captured key is exactly a getQuestions key, and nothing here touches
questions/questionInteractions, which the panel's write-back relies on.
Nothing is clicked, typed or navigated.
*/
const NOTE_VIEW_SELECTOR =
  '.dx-item.dx-multiview-item.dx-item-selected:has(.dx-tabpanel-tabs)';
const NOTE_TAB_PATTERN = /^(.+) - (\d{1,2}\/\d{1,2}\/\d{4})$/;
const PATIENT_TAB_PATTERN = /^(.+) - (\S+)$/;
const CAPTURE_SECTION = 'note';
const TAB_SELECTOR = '.dx-item.dx-tab.dx-tab-selected';

function tabLabel(tab: Element) {
  return (
    tab.getAttribute('data-automation') ||
    tab.querySelector('.dx-template-wrapper span')?.textContent ||
    tab.querySelector('span')?.textContent ||
    ''
  )
    .trim()
    .replace(/\s+/g, ' ');
}

function isHidden(element: Element) {
  return Boolean(element.closest('.dx-multiview-item-hidden'));
}

/** The selected note tab, e.g. "PT Progress Note - 6/17/2026"; null when the
 * chart shows the Patient Navigator, or when more than one note tab claims it. */
function selectedNoteTab(noteView: Element) {
  const labels = new Set<string>();
  noteView.querySelectorAll(TAB_SELECTOR).forEach((tab) => {
    const label = tabLabel(tab);
    if (NOTE_TAB_PATTERN.test(label) && !isHidden(tab)) labels.add(label);
  });
  return labels.size === 1 ? Array.from(labels)[0] : null;
}

/** The open patient's tab, e.g. "John Doe - 3918"; null unless exactly one. */
function selectedPatientTab(noteView: Element) {
  const labels = new Set<string>();
  document.querySelectorAll(TAB_SELECTOR).forEach((tab) => {
    if (noteView.contains(tab) || isHidden(tab)) return;
    const label = tabLabel(tab);
    if (PATIENT_TAB_PATTERN.test(label) && !NOTE_TAB_PATTERN.test(label)) {
      labels.add(label);
    }
  });
  return labels.size === 1 ? Array.from(labels)[0] : null;
}

function strictNoteView() {
  return document.querySelector(NOTE_VIEW_SELECTOR);
}

/** The patient and the note tab on screen, or null when either is unclear. */
function readNoteIdentity() {
  const noteView = strictNoteView();
  if (!noteView) return null;
  const noteLabel = selectedNoteTab(noteView);
  const patientLabel = selectedPatientTab(noteView);
  if (!noteLabel || !patientLabel) return null;
  const [, name, emrId] = patientLabel.match(PATIENT_TAB_PATTERN)!;
  const [, noteType, visitDate] = noteLabel.match(NOTE_TAB_PATTERN)!;
  return { noteView, noteLabel, name, emrId, noteType: noteType.trim(), visitDate };
}

// Keyed by the chart's patient id and the note tab's own label (type + date of
// service). Not by DOB (a default is substituted when the chart shows none) and
// not by the visit's name. Two notes of one type for one patient on one day
// cannot be told apart by the page, and share a key.
function getNoteKeyForFocusCapture(): string | null {
  const identity = readNoteIdentity();
  return identity
    ? `${window.location.hostname}|patient:${identity.emrId}|note:${identity.noteLabel}`
    : null;
}

function getPatientInfoForFocusCapture() {
  const identity = readNoteIdentity();
  const nameParts = (identity?.name || '').split(' ').filter(Boolean);
  return {
    'EMR ID': identity?.emrId ?? null,
    'First Name': nameParts.length >= 2 ? nameParts[0] : null,
    'Last Name': nameParts.length >= 2 ? nameParts[nameParts.length - 1] : null,
    // Not read: the page lists every open patient's birthdate, and
    // getPatientInfo's fallback is a made-up date. Nothing downstream needs it.
    DOB: null
  };
}

function getPageInfoForFocusCapture() {
  const identity = readNoteIdentity();
  return {
    URL: window.location.origin,
    'Note Type': identity?.noteType ?? null,
    // Notes.py stores this as the visit date.
    'Date of Visit': identity?.visitDate ?? null
  };
}

// DevExtreme's own busy indicator: a grid mid-load must not be saved as if the
// clinician had emptied it.
function isNoteLoading(noteView: Element) {
  return Array.from(
    noteView.querySelectorAll('.dx-loadindicator-visible, .dx-loadpanel-content')
  ).some((indicator) => (indicator as any).checkVisibility?.() !== false);
}

function capturedFlexValue(structure: Record<string, any>) {
  const rows: Record<string, any> = {};
  Object.entries(structure || {}).forEach(([row, fields]) => {
    rows[row] = {};
    Object.entries(fields as Record<string, any>).forEach(([field, entry]) => {
      rows[row][field] = entry?.['Existing Value'] ?? '';
    });
  });
  return rows;
}

function readSectionForFocusCapture(section: string): SectionRead | null {
  // Never while Comprehend itself is writing into the note: those reads would
  // compete with the generation and record half-written values. Comprehend
  // links its own result to the note when it finishes.
  if (section !== CAPTURE_SECTION || overlayRefCount > 0) return null;
  const noteView = strictNoteView();
  if (!noteView || isNoteLoading(noteView)) return null;

  const flex = flexEntriesFromDocument(true, true);
  const taken = new Set(flex.map((entry) => entry.key));
  const values: Record<string, any> = {};
  flex.forEach((entry) => {
    values[entry.key] = capturedFlexValue(entry.structureForApi);
  });
  collectTextQuestions(noteView, (key) => taken.has(key)).forEach(
    ({ key, existingNote }) => {
      values[key] = existingNote;
    }
  );
  return Object.keys(values).length > 0 ? { values } : null;
}

/*
The note toolbar's "Sign and Lock" button is a DevExtreme button carrying
data-automation="Sign and Lock Button". A click on it (or on its label or ink
ripple) is the clinician signing the note off; a disabled button does nothing,
so it does not count.
*/
const SIGN_AND_LOCK_SELECTOR = '[data-automation="Sign and Lock Button"]';

function isSignAndLockClick(event: Event) {
  if (event.type !== 'click' || !(event.target instanceof Element)) return false;
  const button = event.target.closest(SIGN_AND_LOCK_SELECTOR);
  return Boolean(
    button &&
    !isHidden(button) &&
    !button.closest('.dx-state-disabled, [aria-disabled="true"], [disabled]')
  );
}

export const noteFocusCapture: FocusCaptureSource = {
  getNoteKey: getNoteKeyForFocusCapture,
  // The note's own content item: a different note, or the same chart's
  // Patient Navigator, renders a different one.
  getNoteStamp: () =>
    strictNoteView()?.querySelector(
      '.dx-tabpanel-container .dx-multiview-item.dx-item-selected'
    ) ?? null,
  sectionOf: (node) => {
    const element = node instanceof Element ? node : node.parentElement;
    if (
      !element ||
      !element.closest(NOTE_VIEW_SELECTOR) ||
      isHidden(element) ||
      element.closest('.dx-inkripple, .dx-inkripple-wave')
    ) {
      return null;
    }
    return CAPTURE_SECTION;
  },
  listSections: () => (getNoteKeyForFocusCapture() ? [CAPTURE_SECTION] : []),
  readSection: readSectionForFocusCapture,
  getPatientInfo: getPatientInfoForFocusCapture,
  getPageInfo: getPageInfoForFocusCapture,
  isFinalization: isSignAndLockClick,
  // 'class': DevExtreme shows another note or section by switching classes
  // (tab selected, item hidden) without adding or removing nodes.
  observedAttributes: ['aria-checked', 'aria-selected', 'value', 'class']
};

export async function getLogFromInjector() {
  try {
    const htmlContent = document.documentElement.outerHTML;
    chrome.runtime.sendMessage({
      type: 'INJECTOR_ERROR',
      error: { htmlContent: htmlContent }
    });
    return { htmlContent: htmlContent };
  } catch (error) {
    // Return the full HTML content of the document
    console.error('Error occurred while fetching document:', error);
    getLogFromInjector();
  }
}
